import { useState, useEffect } from 'react'

import {
  Drawer,
  TextInput,
  Divider,
  Text,
  ActionIcon,
  Badge,
  Autocomplete,
  Loader,
} from '@mantine/core'

import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_DOCUMENT_TEMPLATE_PACKAGE,
  GET_DOCUMENT_TEMPLATE_PACKAGES,
  UPDATE_DOCUMENT_TEMPLATE_PACKAGE,
  ADD_DOCUMENT_TEMPLATE_PACKAGE,
  CONNECT_DOCUMENT_TEMPLATE,
  DISCONNECT_DOCUMENT_TEMPLATE,
} from 'src/graphql/documentTemplatePackages.graphql'
import { GET_DOCUMENT_TEMPLATES } from 'src/graphql/documentTemplates.graphql'
import { DocumentCategoryLabel } from 'src/graphql/types/documents'
import StyledAlert from 'src/components/Feedback/StyledAlert/StyledAlert'
import IconClose from 'src/icons/IconClose'

import DocumentTemplateDrawer from '../../documentTemplatesTab/components/DocumentTemplateDrawer'

interface DocumentPackageDrawerProps {
  opened: boolean
  onClose: () => void
  packageId: string | null
  mode: 'create' | 'edit'
}

const DocumentPackageDrawer = ({
  opened,
  onClose,
  packageId,
  mode,
}: DocumentPackageDrawerProps) => {
  const [packageName, setPackageName] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  )

  const { data, loading } = useQuery(GET_DOCUMENT_TEMPLATE_PACKAGE, {
    variables: { documentTemplatePackageId: packageId },
    skip: !packageId || mode === 'create',
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setPackageName(data?.documentTemplatePackage?.packageName || '')
      setHasChanges(false)
    },
  })

  const { data: templatesData } = useQuery(GET_DOCUMENT_TEMPLATES)

  const [createPackage, { loading: creating }] = useMutation(
    ADD_DOCUMENT_TEMPLATE_PACKAGE,
    {
      refetchQueries: [GET_DOCUMENT_TEMPLATE_PACKAGES],
      onError: () => toast('Unable to create package', 'error'),
      onCompleted: () => {
        toast('Package created', 'success')
        setPackageName('')
        setSearchValue('')
        setHasChanges(false)
        onClose()
      },
    }
  )

  const [updatePackage, { loading: updating }] = useMutation(
    UPDATE_DOCUMENT_TEMPLATE_PACKAGE,
    {
      refetchQueries: [GET_DOCUMENT_TEMPLATE_PACKAGES],
      onError: (error) => toast(error.message, 'error'),
      onCompleted: () => {
        toast('Package updated', 'success')
        setHasChanges(false)
      },
    }
  )

  const [connectDocument, { loading: connecting }] = useMutation(
    CONNECT_DOCUMENT_TEMPLATE,
    {
      refetchQueries: [
        {
          query: GET_DOCUMENT_TEMPLATE_PACKAGE,
          variables: { documentTemplatePackageId: packageId },
        },
        GET_DOCUMENT_TEMPLATE_PACKAGES,
      ],
      onError: () => toast('Unable to add document', 'error'),
      onCompleted: () => {
        setSearchValue('')
        toast('Document added', 'success')
      },
    }
  )

  const [disconnectDocument, { loading: disconnecting }] = useMutation(
    DISCONNECT_DOCUMENT_TEMPLATE,
    {
      refetchQueries: [
        {
          query: GET_DOCUMENT_TEMPLATE_PACKAGE,
          variables: { documentTemplatePackageId: packageId },
        },
        GET_DOCUMENT_TEMPLATE_PACKAGES,
      ],
      onError: () => toast('Unable to remove document', 'error'),
      onCompleted: () => toast('Document removed', 'success'),
    }
  )

  const pkg = data?.documentTemplatePackage
  const allTemplates = templatesData?.documentTemplates || []

  // Filter out templates already in the package
  const currentTemplateIds = new Set(
    pkg?.documentTemplates?.map((d) => d.documentTemplateId) || []
  )
  const availableTemplates = allTemplates.filter(
    (t) => !currentTemplateIds.has(t.documentTemplateId)
  )
  const templateOptions = availableTemplates.map((t) => ({
    value: t.documentTemplateId,
    label: t.documentName,
  }))

  // Reset state when drawer closes
  useEffect(() => {
    if (!opened) {
      setPackageName('')
      setSearchValue('')
      setHasChanges(false)
      setSelectedTemplateId(null)
    }
  }, [opened])

  // Track changes to package name
  const handlePackageNameChange = (value: string) => {
    setPackageName(value)
    if (mode === 'edit' && pkg && value !== pkg.packageName) {
      setHasChanges(true)
    }
  }

  // Handle save
  const handleSave = () => {
    if (!packageName.trim()) {
      toast('Package name is required', 'error')
      return
    }

    if (mode === 'create') {
      createPackage({
        variables: {
          input: {
            status: 'ACTIVE',
            packageName: packageName.trim(),
            signingTool: 'ANVIL',
            signingToolType: 'ETCH',
            documentTemplatesObj: [],
          },
        },
      })
    } else if (pkg) {
      updatePackage({
        variables: {
          documentTemplatePackageId: pkg.documentTemplatePackageId,
          input: { packageName: packageName.trim() },
        },
      })
    }
  }

  const handleAddDocument = (templateId: string) => {
    if (!packageId) return
    connectDocument({
      variables: {
        documentTemplatePackageId: packageId,
        documentTemplateId: templateId,
      },
    })
  }

  const handleRemoveDocument = (templateId: string) => {
    if (!packageId) return
    disconnectDocument({
      variables: {
        documentTemplatePackageId: packageId,
        documentTemplateId: templateId,
      },
    })
  }

  const formatSigners = (signers: string[]) => {
    if (!signers?.length) return null
    return signers.map((signer) => (
      <Badge
        key={signer}
        size="xs"
        variant="light"
        color={signer === 'APPLICANT' ? 'blue' : 'green'}
      >
        {signer === 'APPLICANT' ? 'Applicant' : 'Employee'}
      </Badge>
    ))
  }

  // If a template is selected, show the template drawer instead
  if (selectedTemplateId) {
    return (
      <DocumentTemplateDrawer
        opened={opened}
        onClose={() => setSelectedTemplateId(null)}
        templateId={selectedTemplateId}
        mode="edit"
        onBack={() => setSelectedTemplateId(null)}
      />
    )
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      withCloseButton={false}
      size="30%"
      offset={6}
      radius="md"
      overlayProps={{ backgroundOpacity: 0.2 }}
      classNames={{
        body: 'flex flex-col h-full',
      }}
    >
      <div className="flex h-full flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-doubleNickel-gray-900">
            {mode === 'create' ? 'Create Package' : 'Edit Package'}
          </h2>
          <Button
            text={mode === 'create' ? 'Create' : 'Save'}
            variant="primary"
            onClick={handleSave}
            loading={creating || updating}
            disabled={mode === 'edit' && !hasChanges}
            size="sm"
          />
        </div>

        <Divider variant="dashed" />

        {loading && mode === 'edit' ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader size="sm" color="gray" />
          </div>
        ) : (
          <div className="flex flex-1 flex-col gap-6 overflow-y-auto">
            {/* Overview Section */}
            <div className="space-y-4">
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                OVERVIEW
              </Text>
              <div className="space-y-3">
                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">Package Name</Text>
                  <div className="w-[250px]">
                    <TextInput
                      value={packageName}
                      onChange={(e) => handlePackageNameChange(e.target.value)}
                      placeholder="Enter package name"
                      size="xs"
                      classNames={{
                        input: 'text-xs',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {mode === 'edit' && (
              <>
                <Divider variant="dashed" />

                {/* Documents Section */}
                <div className="space-y-4">
                  <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                    DOCUMENTS
                  </Text>

                  <div className="space-y-3">
                    {/* Add Document */}
                    <Autocomplete
                      placeholder="Search and add document..."
                      data={templateOptions}
                      value={searchValue}
                      onChange={setSearchValue}
                      onOptionSubmit={handleAddDocument}
                      size="xs"
                      disabled={connecting}
                      rightSection={connecting ? <Loader size="xs" /> : null}
                      classNames={{
                        input: 'text-xs',
                      }}
                    />

                    {/* Document List */}
                    {pkg?.documentTemplates?.length === 0 ? (
                      <Text
                        size="xs"
                        className="py-2 text-center text-doubleNickel-gray-400"
                      >
                        No documents in this package
                      </Text>
                    ) : (
                      pkg?.documentTemplates?.map((doc) => (
                        <div
                          key={doc.documentTemplateId}
                          className="flex cursor-pointer items-center justify-between rounded-md border border-doubleNickel-gray-200 p-3 hover:bg-doubleNickel-gray-50"
                          onClick={() =>
                            setSelectedTemplateId(doc.documentTemplateId)
                          }
                        >
                          <div className="flex flex-col gap-1">
                            <Text size="xs" fw={500}>
                              {doc.documentName}
                            </Text>
                            <div className="flex items-center gap-2">
                              <Text
                                size="xs"
                                className="text-doubleNickel-gray-500"
                              >
                                {DocumentCategoryLabel[doc.documentCategory]}
                              </Text>
                              {formatSigners(doc.signers)}
                            </div>
                          </div>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size={24}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRemoveDocument(doc.documentTemplateId)
                            }}
                            disabled={disconnecting}
                          >
                            <IconClose className="h-4 fill-none stroke-doubleNickel-gray-500" />
                          </ActionIcon>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            {mode === 'create' && (
              <StyledAlert
                variant="warning"
                description={
                  <span className="text-xs">
                    You can add documents to this package after creating it.
                  </span>
                }
              />
            )}
          </div>
        )}
      </div>
    </Drawer>
  )
}

export default DocumentPackageDrawer
